/**
 * UpdateBanner — Top sticky banner shown when a newer bundle is detected.
 *
 * Compares the baked-in `VITE_BUILD_ID` (set in .env.production at deploy
 * time) with the live value polled from `/version.json`. When they differ,
 * shows a banner with "Mettre à jour" (reload) and "Plus tard" (dismiss).
 */

import { useEffect, useRef, useState } from 'react'
import { Sparkles, RefreshCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const POLL_MS = 60_000

interface UpdateBannerProps {
  className?: string
}

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [latestBuildId, setLatestBuildId] = useState<string | null>(null)
  const bakedId = useRef<string | null>(
    (import.meta as any).env?.VITE_BUILD_ID ?? null,
  )

  useEffect(() => {
    if (!bakedId.current) return

    let cancelled = false

    const check = async () => {
      try {
        const r = await fetch('/version.json', {
          cache: 'no-store',
          credentials: 'omit',
        })
        if (!r.ok) return
        const data = (await r.json()) as { buildId?: string }
        if (cancelled) return
        if (data.buildId && data.buildId !== bakedId.current) {
          setLatestBuildId(data.buildId)
          setUpdateAvailable(true)
        }
      } catch {
        // ignore transient failures
      }
    }

    check()
    const id = window.setInterval(check, POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [])

  return {
    updateAvailable,
    latestBuildId,
    currentBuildId: bakedId.current,
    reload: () => {
      const url = new URL(window.location.href)
      url.searchParams.set('_t', Date.now().toString())
      window.location.replace(url.toString())
    },
  }
}

export function UpdateBanner({ className }: UpdateBannerProps) {
  const { updateAvailable, reload } = useVersionCheck()
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!dismissed) return
    const id = window.setTimeout(() => setDismissed(false), 10 * 60_000)
    return () => window.clearTimeout(id)
  }, [dismissed])

  if (!updateAvailable || dismissed) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        // R2: below all z-50 Radix overlays so it never clips a modal's title/close
        'sticky top-0 z-30 shrink-0 w-full',
        'flex items-center justify-center gap-3 px-4 py-2',
        'bg-gradient-to-r from-amber-500/15 via-amber-400/20 to-amber-500/15',
        'border-b border-amber-500/30 text-amber-900 dark:text-amber-100',
        'backdrop-blur-sm shadow-sm',
        className,
      )}
    >
      <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-300 shrink-0" />
      <span className="text-[12.5px] font-semibold">
        Une nouvelle version est disponible.
      </span>
      <Button
        size="sm"
        onClick={reload}
        className="h-7 gap-1.5 bg-amber-700 hover:bg-amber-800 text-white text-[11px] rounded-full px-3"
      >
        <RefreshCw className="h-3 w-3" />
        Mettre à jour
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setDismissed(true)}
        className="h-7 gap-1 text-amber-800 dark:text-amber-200 hover:bg-amber-500/10 text-[11px] rounded-full px-2"
        aria-label="Plus tard"
      >
        <X className="h-3 w-3" />
        Plus tard
      </Button>
    </div>
  )
}
